export type EventParkingLayerId =
  | 'wbbParking'
  | 'mbbParking'
  | 'commencementParking'
  | 'convocationHuntsmanParking'
  | 'convocationKingsburyParking'
  | 'convocationStudentUnionParking'
  | 'uFootballParking'
  | 'monsterJamParking'
  | 'supercrossParking'
  | 'stadiumConcertParking'
  | 'uGymnasticsParking'
  | 'hsFootballParking'
  | 'hsBasketballPlayoffParking';

export interface EventParkingDetails {
  /** Title shown at top when this event is active */
  activeTitle: string;
  /**
   * Manual legend items — kept for reference but no longer rendered by the panel.
   * The panel now auto-generates the legend from the layer renderer via the ArcGIS service.
   * @deprecated Use the service renderer instead.
   */
  legend?: {
    label: string;
    color?: string;
    border?: string;
    icon?: string;
    serviceIcon?: { layerIndex: number; matchLabel?: string };
  }[];
  /** Additional notes/alerts */
  notes: string[];
}

export interface EventParkingLayerConfig {
  layerId: EventParkingLayerId;
  /** One map layer ID, or multiple when the event spans several service sublayers */
  mapLayerId: string | string[];
  /** Single map layer ID search result selection should target; falls back to first mapLayerId when omitted */
  searchMapLayerId?: string;
  title: string;
  /** One service sublayer index, or an array when the event spans several sublayers */
  serviceLayerIndex: number | number[];
  /** Correct polygon layer index for alias search result geometry queries; falls back to first serviceLayerIndex if omitted */
  searchLayerIndex?: number;
  /** event_type value(s) in the lot_names alias table (layer 14) — used for search */
  aliasEventType: string | string[];
  /**
   * event_name value(s) to match against the Event Names table (layer 18).
   * Use this when the Event Names table uses different text than `aliasEventType`.
   * Falls back to `aliasEventType` when omitted.
   */
  eventTableName?: string | string[];
  downloadableMapUrl: string;
  details: EventParkingDetails;
  startDate?: Date;
  endDate?: Date;
  /** Whether this event is currently active/visible in the UI */
  enabled: boolean;
}

export const eventParkingColors = {
  generalParkingColor: '#ff7f7f',
  permitParkingColor: '#dccd7d',
  facultyParkingColor: '#dccd7d',
  visitorPayParkingColor: '#9f4a96',
  standardPayParkingColor: '#9f4a96',
  premiumParkingColor: '#94cbec',
  VIPParkingColor: '#94cbec',
  mediaParkingColor: '#5da899',
  workingStaffParkingColor: '#5da899',
  reservedParkingColor: '#7e2954',
  coachesCircleColor: '#7e2954',
  signatureParkingColor: '#dddddd',
  signatureParkingBorder: '#FF0000',
  boxSuiteParkingColor: '#dddddd',
  boxSuiteParkingBorder: '#FF0000',
  adaParkingColor: '#2e2585',
  pitParkingColor: '#337538',
  closedAreaColor: '#337538',
  tailgatingParkingColor: '#e69f00',
  standardDropOffColor: '#e69f00',
  nonPermittedStaffParkingColor: '#0072b2'
}

export const EVENT_PARKING_LAYER_CONFIG: EventParkingLayerConfig[] = [
  {
    layerId: 'wbbParking',
    mapLayerId: 'wbbEventParkingLayer',
    title: "Women's Basketball Parking",
    serviceLayerIndex: 10,
    aliasEventType: 'Womens Basketball',
    eventTableName: "Women's Basketball",
    downloadableMapUrl: 'https://gis.utah.edu/portal/sharing/rest/content/items/6fcade96bad3486bba8f51ecf3fb8cb3/data',
    details: {
      activeTitle: "Women's Basketball Event - Parking Lots",
      legend: [
        { label: 'General Parking - For LDS Garage, Levels 2 & 3 Only', color:  eventParkingColors.generalParkingColor},
        { label: 'Permit Parking - Central Garage, Levels 1-3 Only', color: eventParkingColors.permitParkingColor },
        { label: 'Visitor Pay Parking', color: eventParkingColors.visitorPayParkingColor },
        { label: 'Reserved Parking - For Central Garage, Level 4 Only', color: eventParkingColors.reservedParkingColor },
        { label: 'Media/Officials Parking', color: eventParkingColors.mediaParkingColor },
      ],
      notes: [
        'GUARDSMAN LOT: RVs/Trailers must be parked 3 hours before game time. (RVs/Trailers will be sent to OVERFLOW LOTS the last 3 hours before game time.)',
        'FOR THIS EVENT ONLY: Ride TRAX within campus at no charge',
        'Traffic Alert: 500 South will be reduced to one lane of traffic in both directions from 1300 East to Guardsman Way. Consider an alternate route to save time!',
        'Parking During the Event: On days when school is in session, parking on campus will be limited and not all lots will be available.',
        'ADA Parking: Available free of charge in non-credential lots on a first come, first served basis.',
      ],
    },
    enabled: true,
  },
  {
    layerId: 'mbbParking',
    mapLayerId: ['mbbEventParkingLayer', 'mbbPointsEventParkingLayer'],
    searchMapLayerId: 'mbbEventParkingLayer',
    title: "Men's Basketball Parking",
    serviceLayerIndex: [9, 11],
    searchLayerIndex: 9,
    aliasEventType: 'Mens Basketball',
    eventTableName: "Men's Basketball",
    downloadableMapUrl: 'https://gis.utah.edu/portal/sharing/rest/content/items/fe217d5c9faf483c8ae848e3327e00e0/data',
    details: {
      activeTitle: "Men's Basketball Event - Parking Lots",
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'Permit Parking', color: eventParkingColors.permitParkingColor },
        { label: 'Visitor Pay Parking', color: eventParkingColors.visitorPayParkingColor },
        { label: 'Media Parking', color: eventParkingColors.mediaParkingColor },
        { label: 'Coaches Circle', color: eventParkingColors.coachesCircleColor },
        { label: 'Signature Parking - Central Garage Level 4, LDS Garage Level 3 Only', color: eventParkingColors.signatureParkingColor, border: eventParkingColors.signatureParkingBorder },
      ],
      notes: [
        'GUARDSMAN LOT: RVs/Trailers must be parked 3 hours before game time. (RVs/Trailers will be sent to OVERFLOW LOTS the last 3 hours before game time.)',
        'FOR THIS EVENT ONLY: Ride TRAX within campus at no charge',
        'Traffic Alert: 500 South will be reduced to one lane of traffic in both directions from 1300 East to Guardsman Way. Consider an alternate route to save time!',
        'Parking During the Event: On days when school is in session, parking on campus will be limited and not all lots will be available.',
        'ADA Parking: Available free of charge in non-credential lots on a first come, first served basis.',
      ],
    },
    enabled: true,
  },
  {
    layerId: 'commencementParking',
    mapLayerId: 'commencementEventParkingLayer',
    title: 'Commencement Parking',
    serviceLayerIndex: 0,
    aliasEventType: 'Commencement',
    downloadableMapUrl: 'https://gis.utah.edu/portal/sharing/rest/content/items/f51a820fe7b44ebbb6f515dfb957912f/data',
    details: {
      activeTitle: 'Commencement Event - Parking Lots',
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'Permit Parking', color: eventParkingColors.permitParkingColor },
        { label: 'Visitor Pay Parking', color: eventParkingColors.visitorPayParkingColor },
        { label: 'ADA Parking', color: eventParkingColors.adaParkingColor },
      ],
      notes: [
        'Arrive Early: Parking fills up quickly for commencement ceremonies.',
        'ADA Parking: Available free of charge in designated lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
  {
    layerId: 'convocationHuntsmanParking',
    mapLayerId: 'convocationHuntsmanEventParkingLayer',
    title: 'Convocation Huntsman Parking',
    serviceLayerIndex: 1,
    aliasEventType: 'Convocation Huntsman',
    downloadableMapUrl: 'https://gis.utah.edu/portal/sharing/rest/content/items/8797853c896743119fd45f58a5bcb8ac/data',
    details: {
      activeTitle: 'Convocation Huntsman Event - Parking Lots',
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'Permit Parking', color: eventParkingColors.permitParkingColor },
        { label: 'Visitor Pay Parking', color: eventParkingColors.visitorPayParkingColor },
        { label: 'ADA Parking', color: eventParkingColors.adaParkingColor },
      ],
      notes: [
        'ADA Parking: Available free of charge in designated lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
  {
    layerId: 'convocationKingsburyParking',
    mapLayerId: 'convocationKingsburyEventParkingLayer',
    title: 'Convocation Kingsbury Parking',
    serviceLayerIndex: 16,
    aliasEventType: 'Convocation Kingsbury',
    downloadableMapUrl: 'https://gis.utah.edu/portal/sharing/rest/content/items/8797853c896743119fd45f58a5bcb8ac/data',
    details: {
      activeTitle: 'Convocation Kingsbury Event - Parking Lots',
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'Permit Parking', color: eventParkingColors.permitParkingColor },
        { label: 'Visitor Pay Parking', color: eventParkingColors.visitorPayParkingColor },
        { label: 'ADA Parking', color: eventParkingColors.adaParkingColor },
      ],
      notes: [
        'ADA Parking: Available free of charge in designated lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
  {
    layerId: 'convocationStudentUnionParking',
    mapLayerId: 'convocationStudentUnionEventParkingLayer',
    title: 'Convocation Student Union Parking',
    serviceLayerIndex: 17,
    aliasEventType: 'Convocation Student Union',
    downloadableMapUrl: 'https://gis.utah.edu/portal/sharing/rest/content/items/8797853c896743119fd45f58a5bcb8ac/data',
    details: {
      activeTitle: 'Convocation Student Union Event - Parking Lots',
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'Permit Parking', color: eventParkingColors.permitParkingColor },
        { label: 'Visitor Pay Parking', color: eventParkingColors.visitorPayParkingColor },
        { label: 'ADA Parking', color: eventParkingColors.adaParkingColor },
      ],
      notes: [
        'ADA Parking: Available free of charge in designated lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
  {
    layerId: 'uFootballParking',
    mapLayerId: ['uFootballParkingLayer', 'uFootballParkingLayer2'],
    searchMapLayerId: 'uFootballParkingLayer',
    title: 'U Football Parking',
    serviceLayerIndex: [3, 2],
    searchLayerIndex: 3,
    aliasEventType: 'Football',
    downloadableMapUrl: 'https://gis.utah.edu/portal/sharing/rest/content/items/21f1233047d044d1bdaed8dcfa69ec63/data',
    details: {
      activeTitle: 'U Football Event - Parking Lots',
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'Permit Parking', color: eventParkingColors.permitParkingColor },
        { label: 'Visitor Pay Parking', color: eventParkingColors.visitorPayParkingColor },
        { label: 'ADA Parking', color: eventParkingColors.adaParkingColor },
        { label: 'Shuttle', serviceIcon: { layerIndex: 2 } },
        { label: 'Additional ADA Stalls', serviceIcon: { layerIndex: 2 } },
      ],
      notes: [
        'GUARDSMAN LOT: RVs/Trailers must be parked 3 hours before game time.',
        'FOR THIS EVENT ONLY: Ride TRAX within campus at no charge',
        'Traffic Alert: 500 South will be reduced to one lane of traffic in both directions from 1300 East to Guardsman Way. Consider an alternate route to save time!',
        'Parking During the Event: On days when school is in session, parking on campus will be limited and not all lots will be available.',
          'ADA Parking: Available free of charge in non-credential lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
  {
    layerId: 'monsterJamParking',
    mapLayerId: 'monsterJamEventParkingLayer',
    title: 'Monster Jam Parking',
    serviceLayerIndex: 12,
    aliasEventType: 'Monster Jam',
    downloadableMapUrl: '',
    details: {
      activeTitle: 'Monster Jam Event - Parking Lots',
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'Premium Parking', color: eventParkingColors.permitParkingColor },
        { label: 'ADA Parking', color: eventParkingColors.adaParkingColor },
      ],
      notes: [
        'Arrive Early: Large crowds expected for Monster Jam events.',
        'ADA Parking: Available free of charge in designated lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
  {
    layerId: 'supercrossParking',
    mapLayerId: 'supercrossEventParkingLayer',
    title: 'Supercross Parking',
    serviceLayerIndex: 13,
    aliasEventType: 'Supercross',
    downloadableMapUrl: '',
    details: {
      activeTitle: 'Supercross Event - Parking Lots',
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'Premium Parking', color: eventParkingColors.permitParkingColor },
        { label: 'ADA Parking', color: eventParkingColors.adaParkingColor },
      ],
      notes: [
        'Arrive Early: Large crowds expected for Supercross events.',
        'ADA Parking: Available free of charge in designated lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
  {
    layerId: 'stadiumConcertParking',
    mapLayerId: 'stadiumConcertEventParkingLayer',
    title: 'Stadium Concert Parking',
    serviceLayerIndex: 4,
    aliasEventType: 'Stadium Concert',
    downloadableMapUrl: 'https://gis.utah.edu/portal/sharing/rest/content/items/0e378e88115c4cbeab4d0a4a7f77fc90/data',
    details: {
      activeTitle: 'Stadium Concert Event - Parking Lots',
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'Premium Parking', color: eventParkingColors.permitParkingColor },
        { label: 'ADA Parking', color: eventParkingColors.adaParkingColor },
      ],
      notes: [
        'Arrive Early: Large crowds expected for stadium concert events.',
        'ADA Parking: Available free of charge in designated lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
  {
    layerId: 'uGymnasticsParking',
    mapLayerId: ['uGymnasticsEventParkingLayer', 'uGymnasticsPointsEventParkingLayer'],
    searchMapLayerId: 'uGymnasticsEventParkingLayer',
    title: 'U Gymnastics Parking',
    serviceLayerIndex: [6, 5],
    searchLayerIndex: 6,
    aliasEventType: 'Gymnastics',
    downloadableMapUrl: 'https://gis.utah.edu/portal/sharing/rest/content/items/0d156173afdf451ab21ea0892798b2f6/data',
    details: {
      activeTitle: 'U Gymnastics Event - Parking Lots',
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'Permit Parking', color: eventParkingColors.permitParkingColor },
        { label: 'Visitor Pay Parking', color: eventParkingColors.permitParkingColor },
        { label: 'ADA Parking', color: eventParkingColors.adaParkingColor },
      ],
      notes: [
        'FOR THIS EVENT ONLY: Ride TRAX within campus at no charge',
        'ADA Parking: Available free of charge in designated lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
  {
    layerId: 'hsFootballParking',
    mapLayerId: 'hsFootballEventParkingLayer',
    title: 'High School Football Parking',
    serviceLayerIndex: 8,
    aliasEventType: 'High School Football Playoffs',
    eventTableName: 'High School Football',
    downloadableMapUrl: '',
    details: {
      activeTitle: 'High School Football Event - Parking Lots',
      legend: [
        { label: 'General Parking', color: eventParkingColors.generalParkingColor },
        { label: 'ADA Parking', color: eventParkingColors.adaParkingColor },
      ],
      notes: [
        'Arrive Early and Carpool: Remember: parking is tight. Give yourself plenty of time to make it to the U.!',
        'Parking During the Event: On days when school is in session, parking on campus will be limited and not all lots will be available.',
        'ADA Parking: Available free of charge in non-credential lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
  {
    layerId: 'hsBasketballPlayoffParking',
    mapLayerId: 'hsBasketballPlayoffEventParkingLayer',
    title: 'HS Basketball Playoff Parking',
    serviceLayerIndex: 7,
    aliasEventType: 'High School Basketball Playoffs',
    eventTableName: 'High School Basketball',
    downloadableMapUrl: 'https://gis.utah.edu/portal/sharing/rest/content/items/675a2778a17a4285904488eeae37c66a/data',
    details: {
      activeTitle: 'High School Basketball Playoff - Parking Lots',
      legend: [
        { label: 'Permit Parking', color: eventParkingColors.generalParkingColor },
        { label: 'General Parking', color: eventParkingColors.permitParkingColor },
      ],
      notes: [
        'Arrive Early: High school basketball playoffs attract large crowds.',
        'ADA Parking: Available free of charge in designated lots on a first come, first served basis.',
      ],
    },
    enabled: false,
  },
];

function getRestEndpoint(): string {
  let restEndpointString = 'fmags.fm.utah.edu';
  if (window.location.href === 'https://upark.app.sys.utah.edu/') {
    console.log('CURRENT URL IS ' + window.location.href);
    restEndpointString = 'fmagsdr.fm.utah.edu';
    console.log('NEW REST ENDPOINT IS ' + restEndpointString);
  }
  return restEndpointString;
}

const EVENT_PARKING_SERVICE_PATH = '/arcgis/rest/services/mapservices/allevents/MapServer';

const SURFACE_LOTS_SERVICE_PATH = '/arcgis/rest/services/mapservices/surface_lots/MapServer/0';

export const buildEventParkingServiceUrl = (): string =>
  `https://${getRestEndpoint()}${EVENT_PARKING_SERVICE_PATH}`;

export const buildEventParkingLayerUrl = (serviceLayerIndex: number | number[]): string | string[] => {
  if (Array.isArray(serviceLayerIndex)) {
    return serviceLayerIndex.map((idx) => `${buildEventParkingServiceUrl()}/${idx}`);
  }
  return `${buildEventParkingServiceUrl()}/${serviceLayerIndex}`;
};

export const getEventSearchMapLayerId = (config: EventParkingLayerConfig): string => {
  if (config.searchMapLayerId) {
    return config.searchMapLayerId;
  }

  return Array.isArray(config.mapLayerId) ? config.mapLayerId[0] : config.mapLayerId;
};

export const buildSurfaceLotsUrl = (): string =>
  `https://${getRestEndpoint()}${SURFACE_LOTS_SERVICE_PATH}`;
